import React from 'react'
import Link from 'next/link'
import Image from '@/components/Image'
import { Logo } from '@/components/Logo/Logo'
import { Button } from '@/components/Button'
import { cn } from '@/utilities/cn'

export interface NavigationItem {
  label: string
  url: string
  newTab?: boolean
}

export interface LogoConfig {
  text?: string
  image?: {
    url: string
    alt: string
    width: number
    height: number
  }
  url?: string
}

export interface CTAButton {
  label: string
  url: string
  variant?: 'default' | 'outline' | 'secondary' | 'ghost'
  newTab?: boolean
}

export interface HeaderUIProps {
  logo?: LogoConfig
  navigation?: NavigationItem[]
  ctaButton?: CTAButton
  sticky?: boolean
  backgroundColor?: 'white' | 'dark' | 'primary' | 'transparent'
  className?: string
}

export const HeaderUI: React.FC<HeaderUIProps> = ({
  logo = { url: '/' },
  navigation = [],
  ctaButton,
  sticky = false,
  backgroundColor = 'white',
  className = '',
}) => {
  const backgroundClasses = {
    white: 'bg-white text-slate-900',
    dark: 'bg-slate-900 text-white',
    primary: 'bg-primary text-white',
    transparent: 'bg-transparent',
  }

  const stickyClasses = sticky ? 'sticky top-0 z-50' : ''

  return (
    <header
      className={cn(
        'border-b border-slate-200',
        backgroundClasses[backgroundColor],
        stickyClasses,
        className,
      )}
    >
      <div className="container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            {logo.url ? (
              <Link href={logo.url} className="flex items-center">
                {logo.image ? (
                  <Image
                    src={logo.image.url}
                    alt={logo.image.alt}
                    width={logo.image.width}
                    height={logo.image.height}
                    className="h-8 w-auto"
                  />
                ) : logo.text ? (
                  <span className="text-xl font-bold">{logo.text}</span>
                ) : (
                  // Eager (above the fold) but not high priority: on mobile the hero image is the
                  // largest paint, and a second high-priority image competes with it.
                  <Logo loading="eager" priority="auto" />
                )}
              </Link>
            ) : logo.image ? (
              <Image
                src={logo.image.url}
                alt={logo.image.alt}
                width={logo.image.width}
                height={logo.image.height}
                className="h-8 w-auto"
              />
            ) : logo.text ? (
              <span className="text-xl font-bold">{logo.text}</span>
            ) : (
              <Logo loading="eager" priority="auto" />
            )}
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigation.map((item, index) => (
              <Link
                key={index}
                href={item.url}
                target={item.newTab ? '_blank' : undefined}
                rel={item.newTab ? 'noopener noreferrer' : undefined}
                className="text-sm font-medium hover:text-primary transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="flex items-center space-x-4">
            {ctaButton && (
              <Button
                variant={ctaButton.variant || 'default'}
                asChild
              >
                <Link
                  href={ctaButton.url}
                  target={ctaButton.newTab ? '_blank' : undefined}
                  rel={ctaButton.newTab ? 'noopener noreferrer' : undefined}
                >
                  {ctaButton.label}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}